import { useState, useEffect, useRef } from "react"
import { X, Loader2, Calendar, MapPin, Check } from "lucide-react"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

// "123456" -> "12:34.56"
const formatSeedTime = (val) => {
  const d = val.replace(/\D/g, "").slice(0, 6)
  return d.slice(0, 2) + (d.length > 2 ? ":" + d.slice(2, 4) : "") + (d.length > 4 ? "." + d.slice(4, 6) : "")
}

export default function RegistrationModal({ open, onClose, event, existingRegistration, onRegisterSuccess }) {
  const { t } = useLanguage()
  const [selectedSubEvents, setSelectedSubEvents] = useState([])
  const [seedTimes, setSeedTimes] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const panelRef = useRef(null)

  useEffect(() => {
    if (open) {
      setSelectedSubEvents([])
      setSeedTimes({})
      setError("")
      setIsSubmitting(false)
    }
  }, [open, event])

  // Escape closes, background stops scrolling, focus moves into the dialog and returns on close.
  useEffect(() => {
    if (!open || !event) return
    const opener = document.activeElement
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    panelRef.current?.focus()
    const onKey = (e) => e.key === "Escape" && !isSubmitting && onClose?.()
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = prevOverflow
      opener?.focus?.()
    }
  }, [open, event, isSubmitting, onClose])

  if (!open || !event) return null

  const availableSubs = event.available_sub_events || []
  const existingSubIds = existingRegistration ? (existingRegistration.sub_events || []).map((se) => se.id) : []
  const fullyRegistered = !!existingRegistration && availableSubs.every((se) => existingSubIds.includes(se.id))

  const toggleSubEvent = (id) => {
    if (existingSubIds.includes(id)) return
    setSelectedSubEvents((prev) => {
      if (!prev.includes(id)) return [...prev, id]
      setSeedTimes(({ [id]: _, ...rest }) => rest)
      return prev.filter((x) => x !== id)
    })
  }

  const missingSeedTimes = selectedSubEvents.some((id) => (seedTimes[id] || "").length < 8)
  // Base entry alone is a valid registration; an existing one needs something new to add.
  const canSubmit = !isSubmitting && !missingSeedTimes && (selectedSubEvents.length > 0 || !existingRegistration)

  const handleProceed = async (e) => {
    e.preventDefault()
    if (!canSubmit) return
    setIsSubmitting(true)
    setError("")
    try {
      const res = await fetch(`${API}/api/events/${event.id}/register`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" },
        body: JSON.stringify({ sub_events: selectedSubEvents.map((id) => ({ id, seed_time: seedTimes[id] })) }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || t("reg_failed"))
      }
      setIsSubmitting(false)
      onRegisterSuccess()
    } catch (err) {
      setError(err.message)
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(e) => e.target === e.currentTarget && !isSubmitting && onClose?.()}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reg-title"
        tabIndex={-1}
        className="flex max-h-[92dvh] w-full max-w-lg flex-col overflow-y-auto overscroll-contain rounded-t-2xl border border-white/10 bg-ink-900 shadow-2xl outline-none sm:max-h-[85dvh] sm:rounded-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-ink-800 px-5 py-4 sm:px-6">
          <h2 id="reg-title" className="font-display text-xl font-bold text-paper-0">{t("reg_title")}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("reg_close")}
            className="-mr-2 flex h-11 w-11 items-center justify-center rounded-lg text-mist-300 transition-colors hover:text-paper-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleProceed} className="flex flex-col gap-5 p-5 sm:p-6">
          {error && (
            <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-300">
              {error}
            </div>
          )}

          <div className="flex flex-col gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <h3 className="font-display text-lg font-bold text-gold-400">{event.title}</h3>
            <div className="flex items-center gap-2 text-sm text-mist-300">
              <Calendar className="h-4 w-4 shrink-0 text-mist-400" />
              {event.date}
            </div>
            <div className="flex items-center gap-2 text-sm text-mist-300">
              <MapPin className="h-4 w-4 shrink-0 text-mist-400" />
              {event.venue}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-mist-300">{t("reg_options")}</h4>

            <div className={`flex min-h-[52px] items-center gap-3 rounded-lg border p-4 ${existingRegistration ? "border-white/5 bg-white/[0.02]" : "border-gold-500/30 bg-gold-500/5"}`}>
              <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border ${existingRegistration ? "border-transparent bg-emerald-500/20 text-emerald-400" : "border-gold-500 bg-gold-500 text-ink-900"}`}>
                <Check className="h-3.5 w-3.5" />
              </div>
              <span className={`text-sm font-semibold ${existingRegistration ? "text-mist-300" : "text-paper-0"}`}>
                {t("reg_base_entry")}{existingRegistration && ` (${t("reg_registered")})`}
              </span>
            </div>

            {availableSubs.length > 0 && (
              <div className="mt-2 flex flex-col gap-2">
                <h5 className="text-xs font-semibold text-mist-300">{t("reg_additional")}</h5>
                {availableSubs.map((se) => {
                  const isExisting = existingSubIds.includes(se.id)
                  const isSelected = selectedSubEvents.includes(se.id) || isExisting
                  const existingSeed = isExisting && existingRegistration.sub_events.find((x) => x.id === se.id)?.seed_time
                  return (
                    <label
                      key={se.id}
                      className={`flex min-h-[52px] items-start gap-3 rounded-lg border p-4 transition-colors focus-within:ring-2 focus-within:ring-gold-400/50 ${
                        isExisting ? "cursor-not-allowed border-white/5 bg-white/[0.02] opacity-80"
                        : isSelected ? "cursor-pointer border-gold-500/30 bg-gold-500/5"
                        : "cursor-pointer border-white/10 hover:border-white/20 hover:bg-white/[0.02]"
                      }`}
                    >
                      <div className="relative mt-0.5 flex shrink-0 items-center justify-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isExisting}
                          onChange={() => toggleSubEvent(se.id)}
                          className="peer h-5 w-5 appearance-none rounded-sm border border-white/20 bg-ink-900 checked:border-gold-500 checked:bg-gold-500 disabled:border-transparent disabled:bg-emerald-500/20"
                        />
                        {isSelected && (
                          <Check className={`pointer-events-none absolute h-3.5 w-3.5 ${isExisting ? "text-emerald-400" : "text-ink-900"}`} />
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                        <span className={`text-sm font-medium ${isSelected && !isExisting ? "text-paper-0" : "text-mist-300"}`}>
                          {se.name}
                          {isExisting && (
                            <span className="ml-2 rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                              {t("reg_registered")}
                            </span>
                          )}
                        </span>
                        {isSelected && !isExisting && (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-mist-300">{t("reg_seed_time")}:</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              placeholder="00:00.00"
                              value={seedTimes[se.id] || ""}
                              onChange={(e) => setSeedTimes((p) => ({ ...p, [se.id]: formatSeedTime(e.target.value) }))}
                              className={`h-10 w-28 rounded border bg-ink-900 px-2 font-mono text-sm text-paper-0 outline-none focus:border-gold-500/60 ${(seedTimes[se.id] || "").length === 8 ? "border-emerald-500/50" : "border-white/20"}`}
                            />
                          </div>
                        )}
                        {isExisting && (
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-mist-300">{t("reg_seed_time")}:</span>
                            <span className="font-mono text-xs text-mist-300">{existingSeed || t("reg_seed_time_na")}</span>
                          </div>
                        )}
                      </div>
                    </label>
                  )
                })}
              </div>
            )}
          </div>

          <div className="sticky bottom-0 -mx-5 -mb-5 border-t border-white/10 bg-ink-900 p-5 sm:-mx-6 sm:-mb-6 sm:p-6">
            {fullyRegistered ? (
              <div className="rounded-lg bg-emerald-500/10 px-4 py-3 text-center text-sm font-medium text-emerald-400">
                {t("reg_all_done")}
              </div>
            ) : (
              <button
                type="submit"
                disabled={!canSubmit}
                className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-gold-500 py-3 text-sm font-bold text-ink-900 transition-colors hover:bg-gold-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-300 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("reg_processing")}</> : t("reg_confirm")}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
